import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import {
  SFPageContainer,
  SFCheckIcon,
  SFCard,
  SFCardBody,
  SFSkeleton,
  SFTypography,
  getStyled,
  SFActionBar,
  ACTION_BAR_ACTION_NAMES,
  ACTION_BAR_ACTION_TYPES,
} from "@sf-core";
import { toastActions } from "@base-provider";
import AddUserTenantForm from "./addUserTenantForm";
import {
  addTenantToUser,
  getAllTenants,
  getUserTenants,
} from "../../../services/tenantUserService";
import { getUser } from "../../../services/userService";
import { PATHS } from "../../../constants/paths";

const { styled } = getStyled();

const SFCardStyled = styled(SFCard)(() => ({
  border: "none",
}));

const AddUserTenant = () => {
  const [form, setForm] = useState({
    data: { tenants: null, tenantId: "", userRoleId: "", expiresAt: null },
    errors: [],
  });
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const filterTenants = (allTenants, userTenants) => {
    let tenants = allTenants.filter((tenant) => {
      if (
        typeof userTenants.find(
          (userTenant) => userTenant.tenantId === tenant.id
        ) === "undefined"
      ) {
        return true;
      }
      return false;
    });
    return tenants;
  };

  useEffect(async () => {
    const allTenantsRes = await getAllTenants();
    const userTenantsRes = await getUserTenants(id);
    const userRes = await getUser(id);
    setForm((curState) => ({
      ...curState,
      data: {
        ...curState.data,
        email: userRes.data.email,
        name: userRes.data.name,
        userName: userRes.data.userName,
        tenants: filterTenants(allTenantsRes.data, userTenantsRes.data),
      },
    }));
  }, []);

  const addTenantHandler = async () => {
    if (form.errors.length > 0) {
      dispatch(
        toastActions.showErrorMessage(
          t("usersScreen.userTenantScreen.invalidFieldsMessage")
        )
      );
      return;
    }
    await addTenantToUser({
      userId: id,
      tenantId: form.data.tenantId,
      tenantUserRoleId: form.data.tenantUserRoleId,
      expiresAt: form.data.expiresAt,
      name: form.data.name,
      userName: form.data.userName,
      email: form.data.email,
    });
    dispatch(
      toastActions.showSuccessMessage(
        t("tenantUsersScreen.messages.addSuccess")
      )
    );
    navigate(PATHS.ADMIN_USERS_USER_PROFILE.replace(/:id/, id));
  };

  const formChangeHandler = ({ curData, prevData, errors }) => {
    setForm((curState) => ({ ...curState, data: curData, errors: errors }));
  };

  const leaveHandler = () =>
    navigate(PATHS.ADMIN_USERS_USER_PROFILE.replace(/:id/, id));

  const actionDefs = [
    {
      name: ACTION_BAR_ACTION_NAMES.CANCEL,
      type: ACTION_BAR_ACTION_TYPES.BUTTON,
      onClick: leaveHandler,
      buttonProps: {
        label: t("components.toolbar.templates.cancel"),
        variant: "secondary",
      },
    },
    {
      name: ACTION_BAR_ACTION_NAMES.SAVE,
      type: ACTION_BAR_ACTION_TYPES.BUTTON,
      onClick: addTenantHandler,
      buttonProps: {
        label: t("usersScreen.userProfileScreen.saveButton"),
        variant: "primary",
        icon: <SFCheckIcon size="small" />,
        iconPlacement: "start",
        async: true,
      },
      confirmation: true,
      confirmationProps: {
        header: t("usersScreen.userTenantScreen.addUserToTenantHeaderConfirmMessage"),
        message: t("usersScreen.userTenantScreen.addUserToTenantConfirmMessage"),
      }
    },
  ];

  return (
    <>
      <SFPageContainer disableGutters>
        {form.data.tenants?.length > 0 && (
          <SFActionBar
            actionDefs={actionDefs}
            titleProps={{
              title: t("adminRoutes.addTenantToUser"),
            }}
          />
        )}
        <SFCardStyled>
          <SFCardBody>
            {!form.data.tenants && <SFSkeleton />}
            {form.data.tenants?.length === 0 && (
              <SFTypography>
                {t("usersScreen.userTenantScreen.allTenantsAssignedToUser")}
              </SFTypography>
            )}
            {form.data.tenants?.length > 0 && (
              <AddUserTenantForm
                data={form.data}
                onChange={formChangeHandler}
              />
            )}
          </SFCardBody>
        </SFCardStyled>
      </SFPageContainer>
    </>
  );
};

export default AddUserTenant;
